import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Lock, ArrowUpRight, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Separate loading flag for the Google callback so the primary form
  // doesn't visually spin when we're just exchanging a session_id.
  const [googleExchanging, setGoogleExchanging] = useState(false);
  // Idempotent guard against React StrictMode double-invocation of the
  // session-exchange effect (playbook mandates useRef, not useState).
  const exchangeGuard = useRef(false);

  // -- Emergent Google Auth callback handler --
  // After the user completes consent on https://auth.emergentagent.com/,
  // the browser lands back on THIS page with `#session_id=<uuid>` in the
  // URL fragment. We swap that server-side for an aigo_admin cookie.
  useEffect(() => {
    if (exchangeGuard.current) return;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) return;
    exchangeGuard.current = true;

    const sessionId = decodeURIComponent(match[1]);
    // Strip the fragment immediately so a refresh doesn't re-fire the exchange.
    window.history.replaceState(null, "", window.location.pathname);

    (async () => {
      setGoogleExchanging(true);
      setError("");
      try {
        const { data } = await axios.post(
          `${API}/auth/google/session`,
          null,
          {
            headers: { "X-Session-ID": sessionId },
            withCredentials: true,
          },
        );
        localStorage.setItem("aigo_admin_email", data.user.email);
        navigate("/admin", { replace: true });
      } catch (e) {
        setError(
          formatApiErrorDetail(e.response?.data?.detail) ||
            "Google sign-in failed. Try again or use email + password below.",
        );
      } finally {
        setGoogleExchanging(false);
      }
    })();
  }, [navigate]);

  const startGoogleSignIn = () => {
    setError("");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
    // URLS, THIS BREAKS THE AUTH. Derived from window.location.origin so
    // the same code works in preview and production without env-var mismatch.
    const redirectUrl = `${window.location.origin}/admin/login`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // Cookie-based session: `withCredentials: true` lets the browser
      // receive + persist the HttpOnly `aigo_admin` cookie set by the
      // backend. No token needs to be stored in localStorage.
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true },
      );
      // Keep the admin's email in localStorage for UX (topbar "Signed in as …") —
      // this is public info, not a credential.
      localStorage.setItem("aigo_admin_email", data.user.email);
      navigate("/admin", { replace: true });
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-root"
         className="min-h-screen bg-slate-50 antialiased relative overflow-hidden"
         style={{ fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif" }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap" />

      {/* Decorative emerald spotlight */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full opacity-50"
           style={{ background: "radial-gradient(closest-side, rgba(16,185,129,0.18), transparent 70%)" }} />
      <div className="pointer-events-none absolute -bottom-40 -right-20 w-[600px] h-[600px] rounded-full opacity-40"
           style={{ background: "radial-gradient(closest-side, rgba(52,211,153,0.18), transparent 70%)" }} />

      <div className="relative min-h-screen grid lg:grid-cols-5">
        {/* Brand panel — desktop only */}
        <aside className="hidden lg:flex lg:col-span-2 flex-col justify-between p-10 xl:p-14 border-r border-slate-200/70 bg-white/40 backdrop-blur-sm">
          <Link to="/" className="inline-flex items-center gap-3 group">
            <img src="/logo.png" alt="AI Growth Operator" className="w-11 h-11 rounded-xl object-contain shadow-sm group-hover:scale-105 transition" />
            <div>
              <div className="text-slate-900 font-bold tracking-tight">AI Growth Operator</div>
              <div className="text-emerald-700 text-[11px] uppercase tracking-[0.18em] font-bold mt-0.5">Admin Console</div>
            </div>
          </Link>
          <div>
            <div className="text-emerald-700 text-[11px] uppercase tracking-[0.2em] font-bold mb-3">Today</div>
            <h2 className="text-3xl xl:text-4xl text-slate-900 font-extrabold tracking-tight leading-[1.05]">
              Your <span className="text-emerald-600">operator</span> is awake.
            </h2>
            <p className="mt-3 text-slate-600 text-sm leading-relaxed max-w-md">
              Leads captured, waitlist joins tallied, briefings queued for 7 AM. Sign in to see what changed while you slept.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              {[
                { k: "Leads", v: "live" },
                { k: "Revenue", v: "live" },
                { k: "MRR", v: "live" },
              ].map((p) => (
                <div key={p.k} className="rounded-xl border border-slate-200/70 bg-white/70 px-3 py-2.5">
                  <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500">{p.k}</div>
                  <div className="mt-1 inline-flex items-center gap-1.5 text-emerald-700 text-xs font-bold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> {p.v}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-[11px] text-slate-500">© {new Date().getFullYear()} AI Growth Operator</div>
        </aside>

        {/* Login card */}
        <main className="lg:col-span-3 flex items-center justify-center px-6 py-12 lg:py-0">
          <div className="w-full max-w-md">
            {/* Mobile brand mark */}
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <img src="/logo.png" alt="AI Growth Operator" className="w-10 h-10 rounded-xl object-contain" />
              <div>
                <div className="text-slate-900 font-bold tracking-tight">AI Growth Operator</div>
                <div className="text-emerald-700 text-[11px] uppercase tracking-[0.15em] font-bold">Admin Console</div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-emerald-500/10 p-8">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-700 font-bold tracking-[0.12em] uppercase mb-5">
                <Lock className="w-3 h-3" /> Restricted area
              </div>
              <h1 className="text-3xl text-slate-900 tracking-tight font-extrabold mb-2 leading-tight">Sign in to your dashboard</h1>
              <p className="text-slate-600 text-sm mb-6 leading-relaxed">Admin-only access to leads, waitlist, briefings, and Google Ads.</p>

              {/* Google sign-in — primary path for allowlisted admins */}
              <button
                type="button"
                onClick={startGoogleSignIn}
                disabled={googleExchanging || loading}
                data-testid="admin-google-signin"
                className="w-full inline-flex items-center justify-center gap-3 px-5 py-3 rounded-full border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-60 text-slate-800 font-semibold text-sm shadow-sm transition"
              >
                {googleExchanging ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                )}
                <span>{googleExchanging ? "Signing you in…" : "Sign in with Google"}</span>
              </button>

              {/* Divider */}
              <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400">
                <div className="flex-1 h-px bg-slate-200" />
                <span>or sign in with email</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <form onSubmit={submit} className="space-y-3" data-testid="admin-login-form">
                <div>
                  <label className="block text-[11px] font-bold tracking-[0.1em] text-slate-500 uppercase mb-1.5">Email</label>
                  <input
                    data-testid="admin-login-email"
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@..." autoComplete="username"
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 text-sm focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold tracking-[0.1em] text-slate-500 uppercase mb-1.5">Password</label>
                  <input
                    data-testid="admin-login-password"
                    type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" autoComplete="current-password"
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 text-sm focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 outline-none transition"
                  />
                </div>
                <button
                  data-testid="admin-login-submit"
                  type="submit" disabled={loading}
                  className="group w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-semibold text-sm shadow-lg shadow-emerald-500/30 transition mt-3">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" /></>}
                </button>
                {error && (
                  <div data-testid="admin-login-error"
                       className="mt-3 px-3 py-2.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold">
                    ⚠ {error}
                  </div>
                )}
              </form>

              <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-2 text-[11px] text-slate-500">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                Tokens expire after 24 hours · 5 failed attempts = 15 min lockout
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLogin;
